<script setup lang="ts">import { $api } from "@client/lib/trpc";import { useUserStore as ___useUserStore } from "@client/stores"; const $$user =  ___useUserStore();import {useRouter as ___useRouter} from "vue-router";const $router = ___useRouter();
import { EyeIcon, EyeOffIcon, XIcon } from "lucide-vue-next";

const shouldShow = defineModel("show", { type: Boolean, required: true });
const visibleComponent = defineModel("component", { type: String, required: true });

const form = ref(getInitialFormData()),
	errorMessage = ref<string>(),
	showPassword = ref(false);

function getInitialFormData() {
	return { username: "", email: "", accessCode: "", password: "", university: "" };
}

const handleRegister = async () => {
	try {
		if (!canRegister.value) return;
		const user = await $api.auth.register.mutate({
			accessCode: form.value.accessCode,
			email: form.value.email,
			password: form.value.password,
			username: form.value.username,
			university: form.value.university,
		});

		$$user.saveCredentials(user);
		$$user.friendList = await $api.friends.list.query();
		resetForm();
		shouldShow.value = false;
		$router.push({ name: "lobby" });
	} catch (error: any) {
		errorMessage.value = error.data.message;
	}
};

const resetForm = () => (form.value = getInitialFormData());
const canRegister = computed(
	() =>
		form.value.username &&
		form.value.email &&
		form.value.password &&
		form.value.accessCode &&
		form.value.university,
);
function handleRegisterLeave() {
	resetForm();
	visibleComponent.value = "login";
}
const handleGoToLogin = () => {
	resetForm();
	visibleComponent.value = "login";
};

watch(
	() => Object.values(form.value),
	() => (errorMessage.value = ""),
);
</script>
<template>
	<v-dialog
		v-model="shouldShow"
		@after-leave="handleRegisterLeave"
		minWidth="320px"
		maxWidth="35%"
		maxHeight="95%">
		<v-card class="rounded-xl py-5 hide-scrollbar">
			<v-alert
				v-if="errorMessage"
				closable
				class="mx-16 mb-2 py-2 text-body-2 tw:!overflow-auto"
				color="#DD2C00">
				<template #default>
					<pre class="font-rubik tw:whitespace-pre-wrap tw:text-sm tw:capitalize">{{
						errorMessage
							.toLowerCase()
							.split("\n")
							.map((line) => `* ${line}`)
							.join("\n")
					}}</pre>
				</template>
			</v-alert>
			<template #title>
				<div class="d-flex justify-center align-center">
					<span class="tw:text-2xl font-weight-bold tw:basis-full tw:text-center">
						Register
					</span>
					<XIcon
						@click="shouldShow = false"
						class="tw:cursor-pointer tw:hover:scale-125 tw:mr-3"
						:size="20" />
				</div>
			</template>

			<v-card-item class="px-16">
				<v-form @keyup.enter="handleRegister" id="register-form">
					<p>Username</p>
					<v-text-field
						autocomplete="new-username"
						v-model="form.username"
						type="text"
						required
						flat
						variant="solo-filled" />
					<p>Email</p>
					<v-text-field
						autocomplete="none"
						role="presentation"
						type="any"
						v-model="form.email"
						flat
						variant="solo-filled" />
					<p>Password</p>
					<v-text-field
						v-model="form.password"
						autocomplete="off"
						required
						aria-autocomplete="none"
						hint="At least 12 characters"
						:type="showPassword ? 'text' : 'password'"
						flat
						variant="solo-filled">
						<template #append-inner>
							<component
								@click="showPassword = !showPassword"
								:is="showPassword ? EyeOffIcon : EyeIcon"
								class="tw:cursor-pointer"
								:size="20" />
						</template>
					</v-text-field>
					<p>University</p>
					<v-text-field
						autocomplete="off"
						type="any"
						v-model="form.university"
						required
						flat
						variant="solo-filled" />
					<p>Access Code</p>
					<v-text-field
						autocomplete="off"
						type="any"
						v-model="form.accessCode"
						required
						flat
						variant="solo-filled" />
				</v-form>

				<div class="d-flex flex-column ga-2 px-2">
					<v-btn
						:disabled="!canRegister"
						type="submit"
						form="register-form"
						rounded
						class="mx-1 text-none text-body-1 font-rubik"
						color="primary"
						height="50"
						@click.prevent="handleRegister">
						Register
					</v-btn>
					<p class="pt-2 text-center tw:text-sm text-medium-emphasis">
						Already have an a ccount?
						<button @click="handleGoToLogin" class="text-high-emphasis cursor-pointer">
							Login
						</button>
					</p>
				</div>
			</v-card-item>
		</v-card>
	</v-dialog>
</template>
